/**
 * DialogShell — reusable primitives for the standard QHS dialog
 * header / body / footer composition.
 *
 * The Dialog component provides raw slots; these primitives encode
 * the spacing, icon-badge, typography, and hint-box patterns shared
 * by CreateAccountModal, instant-assessment forms, and any future
 * form dialog. Compose them inside Dialog's `header` / `footer`
 * props and wrap body content in `DialogBody`:
 *
 *   <Dialog
 *     open={open}
 *     onClose={close}
 *     header={
 *       <DialogHeader>
 *         <DialogIconBadge><BuildingIcon /></DialogIconBadge>
 *         <DialogTitleBlock>
 *           <DialogTitle>Create account</DialogTitle>
 *           <DialogDescription>Set up the new org…</DialogDescription>
 *         </DialogTitleBlock>
 *       </DialogHeader>
 *     }
 *     footer={
 *       <DialogFooter>
 *         <DialogFooterStepText>Step 1 of 2</DialogFooterStepText>
 *         <DialogFooterActions>
 *           <Button variant="ghost">Cancel</Button>
 *           <Button variant="primary">Create</Button>
 *         </DialogFooterActions>
 *       </DialogFooter>
 *     }
 *   >
 *     <DialogBody>{fields}</DialogBody>
 *   </Dialog>
 */
import { styled } from '../styled';

export const DialogHeader = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 16,
  padding: '24px 28px 20px',
});

export const DialogIconBadge = styled('div')({
  width: 44,
  height: 44,
  borderRadius: 10,
  background: 'var(--qhs-brand-tint-8)',
  color: 'var(--qhs-brand)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& svg': { width: 22, height: 22 },
});

export const DialogTitleBlock = styled('div')({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});

export const DialogTitle = styled('h3')({
  margin: 0,
  fontFamily: 'var(--qhs-font-head)',
  fontSize: 20,
  fontWeight: 700,
  color: 'var(--qhs-text-1)',
  letterSpacing: '-0.3px',
});

export const DialogDescription = styled('p')({
  margin: 0,
  fontSize: 13,
  lineHeight: 1.5,
  color: 'var(--qhs-text-2)',
});

export const DialogBody = styled('div')({
  padding: '20px 28px 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  flex: 1,
  minHeight: 0,
  height: '100%',
  maxHeight: '100%',
});

// In-body sub-section heading (e.g. "Address" inside a create form).
// Kept lighter than DialogTitle so it visually reads as a sub-group
// under the dialog's main title, not a competing heading.
export const DialogSectionTitle = styled('div')({
  marginTop: 20,
  marginBottom: 2,
  fontFamily: 'var(--qhs-font-head)',
  fontSize: 14,
  fontWeight: 700,
  color: 'var(--qhs-text-1)',
  letterSpacing: '-0.1px',
});

export const DialogSectionHint = styled('div')({
  marginBottom: 10,
  fontSize: 12,
  color: 'var(--qhs-text-3)',
});

// Inline info strip for hints that belong next to a field rather
// than above a section (e.g. "will auto-close after reaching limit").
export const DialogHintBox = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '10px 12px',
  borderRadius: 8,
  background: 'var(--qhs-bg)',
  color: 'var(--qhs-text-2)',
  fontSize: 12,
  lineHeight: 1.5,
  '& svg': {
    width: 16,
    height: 16,
    flexShrink: 0,
    marginTop: 1,
    color: 'var(--qhs-text-3)',
  },
});

export const DialogFooter = styled('div')({
  padding: '16px 28px 20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
});

export const DialogFooterStepText = styled('span')({
  fontSize: 12,
  color: 'var(--qhs-text-3)',
});

export const DialogFooterActions = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  marginLeft: 'auto',
});

export const InfoIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);
